import React from 'react';
import { airlineLogoUrl } from '../lib/airlineLogo';
import { formatMoney } from '../lib/checkoutFormat';

export type BookingTabChipVariant = 'shortest' | 'cheapest';

export type BookingTabItem = {
  airline: string;
  flightNumber?: string;
  price: number;
  /** 1-based tab index shown as "1. Delta Air Lines" */
  index: number;
  selected?: boolean;
  chip?: { label: string; variant: BookingTabChipVariant };
};

export type BookingTabBarProps = {
  tabs: BookingTabItem[];
  onSelect?: (index: number) => void;
};

function ShortestChipIcon() {
  return (
    <svg
      className="booking-tab-bar__chip-svg"
      focusable="false"
      viewBox="0 0 19 14"
      aria-hidden="true"
      width={19}
      height={14}
    >
      <path
        fill="currentColor"
        d="M3.111 3.112V1.557H0v1.555zm8.552 4.671c.203 0 .402-.08.553-.228l2.332-2.333-1.098-1.104-1.994 1.993-1.877-.623-.493 1.479 2.333.778a.79.79 0 0 0 .244.038zM3.11 12.445v-1.556H0v1.556zm8.555-10.11a4.666 4.666 0 1 1 0 9.332 4.667 4.667 0 0 1 0-9.332zm0 11.665a7 7 0 1 0 0-14C7.777 0 5.6 2.005 3.89 3.112l1.555 1.556L0 7l5.444 2.333-1.555 1.555C5.599 11.995 8.555 14 11.666 14z"
      />
    </svg>
  );
}

function CheapestChipIcon() {
  return (
    <svg
      className="booking-tab-bar__chip-svg"
      focusable="false"
      viewBox="0 0 9 14"
      aria-hidden="true"
      width={9}
      height={14}
    >
      <path
        fill="currentColor"
        d="M6.623 6.36c-.653-.304-1.347-.535-2.034-.775-.398-.14-.78-.301-1.116-.527-.663-.445-.536-1.168.24-1.454.22-.08.45-.107.684-.119.9-.04 1.756.099 2.57.428.405.165.54.113.677-.242.144-.376.264-.758.398-1.137.09-.254-.02-.422-.306-.529a7.478 7.478 0 0 0-1.621-.408c-.736-.095-.736-.098-.739-.72C5.373.001 5.373.001 4.328.001c-.151 0-.302-.003-.453 0-.488.012-.57.084-.584.498-.007.185 0 .37-.004.558-.003.55-.007.54-.632.731C1.143 2.251.209 3.118.109 4.506.02 5.736.782 6.565 1.982 7.17c.738.373 1.556.593 2.34.885.305.113.597.243.851.422.753.524.615 1.394-.278 1.723-.478.177-.983.22-1.501.165-.8-.084-1.567-.26-2.289-.575-.422-.185-.546-.136-.69.249-.124.332-.234.667-.344 1.003-.147.451-.092.558.42.769.652.266 1.35.402 2.06.497.557.076.575.096.582.582.003.22.003.442.006.662.004.277.162.44.502.445.385.006.773.006 1.158-.003.316-.006.477-.15.477-.42 0-.3.018-.603.004-.904-.017-.307.14-.463.491-.544.808-.185 1.495-.55 2.024-1.093 1.47-1.503.91-3.704-1.172-4.672z"
      />
    </svg>
  );
}

function TabChip({ label, variant }: { label: string; variant: BookingTabChipVariant }) {
  return (
    <li className={`booking-tab-bar__chip booking-tab-bar__chip--${variant}`}>
      <span className="booking-tab-bar__chip-text">{label}</span>
      <span className="booking-tab-bar__chip-icon" aria-hidden>
        {variant === 'cheapest' ? <CheapestChipIcon /> : <ShortestChipIcon />}
      </span>
    </li>
  );
}

function BookingTab({ tab, onSelect }: { tab: BookingTabItem; onSelect?: () => void }) {
  const logo = airlineLogoUrl(tab.flightNumber ?? tab.airline);
  const selected = tab.selected !== false;

  return (
    <div
      className={`booking-tab-bar__tab${selected ? ' is-selected' : ''}`}
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
    >
      <button
        type="button"
        className="booking-tab-bar__panel"
        onClick={onSelect}
        disabled={!onSelect}
      >
        <div className="booking-tab-bar__panel-inner">
          <div className="booking-tab-bar__copy">
            <p className="booking-tab-bar__name">
              {tab.index}. {tab.airline}
            </p>
            <div className="booking-tab-bar__price">${formatMoney(tab.price)}</div>
          </div>
          {logo ? (
            <div className="booking-tab-bar__logo-wrap">
              <img className="booking-tab-bar__logo" src={logo} alt={tab.airline} />
            </div>
          ) : null}
          {tab.chip ? (
            <ul className="booking-tab-bar__chips">
              <TabChip {...tab.chip} />
            </ul>
          ) : null}
        </div>
      </button>
    </div>
  );
}

export default function BookingTabBar({ tabs, onSelect }: BookingTabBarProps) {
  return (
    <div className="booking-tab-bar">
      <div className="booking-tab-bar__container">
        <div className="booking-tab-bar__shadow-wrap">
          <div className="booking-tab-bar__tabs-root">
            <div className="booking-tab-bar__tabs" role="tablist">
              {tabs.map((tab) => (
                <BookingTab
                  key={tab.index}
                  tab={tab}
                  onSelect={onSelect ? () => onSelect(tab.index) : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
